import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { DRIVER_APPROVAL_REQUESTS_LIST } from '../../graphql/insurancePolicies';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Box, 
  Typography, 
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

const DriverApprovalRequestList = ({ statusFilter, onEdit }) => {
  const { data, loading, error } = useQuery(DRIVER_APPROVAL_REQUESTS_LIST);

  if (loading) return <CircularProgress />;
  if (error) return <div>Error loading requests: {error.message}</div>;

  const requests = data?.driverApprovalRequestsList?.items || [];
  
  // Filter requests by status if statusFilter is provided
  const filteredRequests = statusFilter 
    ? requests.filter(request => request.status === statusFilter)
    : requests;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getEmailStatusIcon = (request) => {
    if (request.emailSentDate) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    return <WarningIcon color="warning" fontSize="small" />;
  };

  const getEmailStatusTooltip = (request) => {
    if (request.emailSentDate) {
      return `Email sent on ${dayjs(request.emailSentDate).format('MMM DD, YYYY')}`;
    }
    return 'Email not sent yet';
  };

  const getBusinessUnitValidation = (request) => {
    const employeeBU = request.employee?.businessUnits?.id;
    const policyBU = request.insurancePolicy?.businessUnits?.id;
    
    if (employeeBU === policyBU) {
      return { valid: true, message: 'Business units match' };
    }
    return { valid: false, message: 'Business units do not match' };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Employee Request History</Typography>
        <Button 
          variant="contained" 
          startIcon={<AssignmentIcon />}
          onClick={() => onEdit?.(null)} // Pass null to indicate new request
        >
          New Driver Request
        </Button>
      </Box>

      {filteredRequests.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No employee requests found{statusFilter ? ` with status "${statusFilter}"` : ''}.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Insurance Policy</TableCell>
              <TableCell>Business Unit</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email Status</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => {
              const buValidation = getBusinessUnitValidation(request);
              
              return (
                <TableRow key={request.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {request.employee?.firstName} {request.employee?.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.employee?.emailAddress}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {request.insurancePolicy?.policyNumber}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.insurancePolicy?.provider}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {buValidation.valid ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                      <Typography variant="body2">
                        {request.employee?.businessUnits?.name || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {dayjs(request.requestDate).format('MMM DD, YYYY')}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={request.status} 
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={getEmailStatusTooltip(request)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getEmailStatusIcon(request)}
                        <Typography variant="caption">
                          {request.emailSentDate ? 'Sent' : 'Pending'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Request">
                        <IconButton 
                          size="small" 
                          onClick={() => onEdit?.(request)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredRequests.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Summary:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${filteredRequests.length}`} 
              size="small" 
              variant="outlined"
            />
            <Chip 
              label={`Pending: ${filteredRequests.filter(r => r.status === 'pending').length}`} 
              size="small" 
              color="warning"
            />
            <Chip 
              label={`Approved: ${filteredRequests.filter(r => r.status === 'approved').length}`} 
              size="small" 
              color="success"
            />
            <Chip 
              label={`Rejected: ${filteredRequests.filter(r => r.status === 'rejected').length}`} 
              size="small" 
              color="error"
            />
            <Chip 
              label={`Emails Sent: ${filteredRequests.filter(r => r.emailSentDate).length}`} 
              size="small" 
              color="info"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DriverApprovalRequestList;
